import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Grid,
  Pagination,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  GridView as GridViewIcon,
} from "@mui/icons-material";

const OrdersDialog = ({ open, onClose, orders = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("chips"); // "chips", "list", "grid"
  const itemsPerPage = 30;

  const title = `Fake Customer Order${orders.length !== 1 ? "s" : ""}`;

  // Filter orders based on search query
  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return orders;
    return orders.filter((order) =>
      order.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [orders, searchQuery]);

  // Paginate filtered orders
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOrders, currentPage]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const renderChipsView = () => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      {paginatedOrders.map((order, index) => (
        <Chip
          key={`${order}-${index}`}
          label={order}
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            height: 36,
            px: 1,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            },
          }}
        />
      ))}
    </Box>
  );

  const renderListView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {paginatedOrders.map((order, index) => (
        <Paper
          key={`${order}-${index}`}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            background: "rgba(102, 126, 234, 0.05)",
            border: "1px solid rgba(102, 126, 234, 0.1)",
            borderRadius: 1,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              background: "rgba(102, 126, 234, 0.1)",
              transform: "translateX(4px)",
            },
          }}
        >
          <AssignmentIcon sx={{ mr: 2, color: "#667eea", fontSize: 20 }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {order}
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  const renderGridView = () => (
    <Grid container spacing={2}>
      {paginatedOrders.map((order, index) => (
        <Grid item xs={12} sm={6} md={4} key={`${order}-${index}`}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
              border: "1px solid rgba(102, 126, 234, 0.2)",
              borderRadius: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.2)",
              },
            }}
          >
            <AssignmentIcon sx={{ mb: 1, color: "#667eea", fontSize: 24 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {order}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderContent = () => {
    switch (viewMode) {
      case "list":
        return renderListView();
      case "grid":
        return renderGridView();
      default:
        return renderChipsView();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 450,
          maxHeight: "85vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header Section */}
      <DialogTitle
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
          position: "relative",
          padding: 3,
          paddingBottom: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 600,
                color: "#2d3748",
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#435674ff",
                fontWeight: 500,
              }}
            >
              Total order{orders.length !== 1 ? "s" : ""}:{" "}
              <strong style={{ color: "#000000ff", fontSize: "1rem" }}>
                {orders.length}
              </strong>
              {filteredOrders.length !== orders.length && (
                <>
                  {" • "}
                  <span style={{ color: "#667eea" }}>
                    Showing: {filteredOrders.length} filtered
                  </span>
                </>
              )}
            </Typography>
          </Box>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                border: "1px solid rgba(102, 126, 234, 0.3)",
                "&.Mui-selected": {
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                },
              },
            }}
          >
            <ToggleButton value="chips">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search Bar */}
        {orders.length > 0 && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search job numbers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#667eea" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.9)",
                },
                "&.Mui-focused": {
                  background: "rgba(255, 255, 255, 1)",
                },
              },
            }}
          />
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          background: "rgba(255, 255, 255, 0.95)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: "8px 24px", flex: 1, overflow: "hidden" }}>
          {filteredOrders.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                color: "#718096",
              }}
            >
              <AssignmentIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                {searchQuery ? "No Matching Orders" : "No Orders"}
              </Typography>
              <Typography variant="body2">
                {searchQuery
                  ? `No orders found matching "${searchQuery}"`
                  : "There are currently no orders to display."}
              </Typography>
            </Box>
          ) : (
            <>
              <Paper
                sx={{
                  maxHeight: 400,
                  overflowY: "auto",
                  p: 3,
                  background:
                    "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                  borderRadius: 2,
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0,0,0,0.1)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a67d8, #6b46c1)",
                    },
                  },
                }}
                elevation={0}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#4a5568",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.75rem",
                    }}
                  >
                    Job Number{paginatedOrders.length > 1 ? "s" : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#667eea", fontWeight: 500 }}
                  >
                    Page {currentPage} of {totalPages} •{" "}
                    {paginatedOrders.length} of {filteredOrders.length}
                  </Typography>
                </Box>
                <Divider
                  sx={{ mb: 2, background: "rgba(102, 126, 234, 0.2)" }}
                />
                {renderContent()}
              </Paper>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        "&.Mui-selected": {
                          background:
                            "linear-gradient(135deg, #667eea, #764ba2)",
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: 3,
          background: "rgba(255, 255, 255, 0.95)",
          borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontWeight: 600,
            px: 2,
            py: 1,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
            transition: "all 0.2s ease-in-out",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrdersDialog;
